/**
 * Display-only removal of the curriculum prefix that level and class names carry
 * ("FBISE Primary", "FBISE Class 1-A", "BISE Punjab Class 2").
 *
 * The prefix stays in the stored names on purpose: the same class name under two boards must
 * not collide on the server, where students and subjects resolve a class by bare name. So
 * `manualData`, lesson-planning keys and every payload keep the full name; only what the user
 * READS goes through `stripCurriculumPrefix`. Never write a stripped name back into state.
 */
import {type CurriculumStructure, classNamePrefixFor} from './academicStructure'

const trim = (value: unknown): string => String(value ?? '').trim()
const lower = (value: unknown): string => trim(value).toLowerCase()

/**
 * Prefixes a name under `curriculum` may start with, longest first: the one derived from the
 * curriculum's own class names (the same rule the level drawer uses when it adds a class),
 * the bracketed short form of the curriculum ("Federal Board (FBISE)" → "FBISE") and the
 * curriculum name itself.
 */
export const curriculumPrefixes = (
  curriculum: unknown,
  classNames: readonly unknown[] = []
): string[] => {
  const name = trim(curriculum)
  if (!name) return []
  const names = classNames.map(trim).filter(Boolean)
  const candidates: string[] = []
  if (names.length > 0) {
    const structure: CurriculumStructure = {
      curriculum: name,
      seededFromDetails: false,
      levels: [
        {
          id: 'display',
          name: '',
          subjects: [],
          classes: names.map((n, i) => ({id: String(i), name: n, sections: []})),
        },
      ],
    }
    candidates.push(classNamePrefixFor(name, structure))
  }
  const bracketed = /\(([^()]+)\)\s*$/.exec(name)
  if (bracketed) candidates.push(trim(bracketed[1]))
  candidates.push(name)
  return Array.from(new Set(candidates.filter(Boolean))).sort((a, b) => b.length - a.length)
}

/**
 * `name` without a leading prefix from `prefixes` (case-insensitive, followed by a space).
 * A name that is only the prefix, or does not start with one, comes back unchanged.
 */
export const stripCurriculumPrefix = (name: unknown, prefixes: readonly string[]): string => {
  const full = trim(name)
  const fullLower = full.toLowerCase()
  for (const prefix of prefixes) {
    const p = lower(prefix)
    if (p && fullLower.startsWith(`${p} `)) {
      const rest = full.slice(p.length).trimStart()
      if (rest) return rest
    }
  }
  return full
}

export type CurriculumPrefixIndex = {
  /** Prefixes per lower-cased curriculum name. */
  byCurriculum: Map<string, string[]>
  /** Every known prefix, longest first — for a name whose curriculum is not at hand. */
  all: string[]
}

/** Build the prefix lookup once from rows that carry `name` (and `grade`) plus `curriculum`. */
export const buildCurriculumPrefixIndex = (
  rows: ReadonlyArray<{name?: unknown; grade?: unknown; curriculum?: unknown}>,
  extraCurricula: readonly unknown[] = []
): CurriculumPrefixIndex => {
  const namesByCurriculum = new Map<string, {curriculum: string; names: string[]}>()
  const add = (curriculum: unknown, ...names: unknown[]) => {
    const c = trim(curriculum)
    if (!c) return
    const entry = namesByCurriculum.get(c.toLowerCase()) ?? {curriculum: c, names: []}
    names.forEach((n) => {
      const t = trim(n)
      if (t) entry.names.push(t)
    })
    namesByCurriculum.set(c.toLowerCase(), entry)
  }
  rows.forEach((row) => add(row?.curriculum, row?.name))
  extraCurricula.forEach((c) => add(c))
  const byCurriculum = new Map<string, string[]>()
  namesByCurriculum.forEach(({curriculum, names}, key) => {
    byCurriculum.set(key, curriculumPrefixes(curriculum, names))
  })
  const all = Array.from(new Set(Array.from(byCurriculum.values()).flat())).sort(
    (a, b) => b.length - a.length
  )
  return {byCurriculum, all}
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Full stored name → display name for every class and level in `rows` whose display form
 * differs, longest full name first. Feeds `stripCurriculumPrefixesInText`.
 */
export const buildDisplayNamePairs = (
  index: CurriculumPrefixIndex,
  rows: ReadonlyArray<{name?: unknown; level?: unknown; curriculum?: unknown}>
): Array<[string, string]> => {
  const pairs = new Map<string, [string, string]>()
  const add = (full: unknown, curriculum: unknown) => {
    const name = trim(full)
    if (!name || pairs.has(name.toLowerCase())) return
    const display = displayCurriculumName(index, name, curriculum)
    if (display !== name) pairs.set(name.toLowerCase(), [name, display])
  }
  rows.forEach((row) => {
    add(row?.name, row?.curriculum)
    add(row?.level, row?.curriculum)
  })
  return Array.from(pairs.values()).sort((a, b) => b[0].length - a[0].length)
}

/**
 * Free text (AI chat turns, server warnings) with every known prefixed class/level name
 * replaced by its display form. Only whole names from `pairs` are touched — never a bare
 * curriculum word — so unrelated text is left exactly as written.
 */
export const stripCurriculumPrefixesInText = (
  text: string,
  pairs: ReadonlyArray<[string, string]>
): string => {
  if (!text || pairs.length === 0) return text
  const byLower = new Map(pairs.map(([full, display]) => [full.toLowerCase(), display]))
  const pattern = new RegExp(
    `(?<![\\p{L}\\p{N}])(${pairs.map(([full]) => escapeRegExp(full)).join('|')})(?![\\p{L}\\p{N}])`,
    'giu'
  )
  return text.replace(pattern, (match) => byLower.get(match.toLowerCase()) ?? match)
}

/**
 * Display text typed or picked by the user → the stored name it stands for: a full stored
 * name is kept, a display name that belongs to exactly one stored name maps back to it, and
 * anything else (a new or unmatched value) is returned as typed.
 */
export const resolveDisplayedName = (
  text: unknown,
  pairs: ReadonlyArray<[string, string]>,
  storedNames: readonly unknown[] = []
): string => {
  const typed = trim(text)
  if (!typed) return typed
  const typedLower = typed.toLowerCase()
  const exact = storedNames.map(trim).find((n) => n.toLowerCase() === typedLower)
  if (exact) return exact
  const matches = Array.from(
    new Set(pairs.filter(([, display]) => display.toLowerCase() === typedLower).map(([f]) => f))
  )
  return matches.length === 1 ? matches[0] : typed
}

/**
 * Display form of a level or class name. With the curriculum known only its prefixes apply;
 * without it every known prefix is tried (still only ever at the very start of the name).
 */
export const displayCurriculumName = (
  index: CurriculumPrefixIndex,
  name: unknown,
  curriculum?: unknown
): string => {
  const prefixes = trim(curriculum)
    ? (index.byCurriculum.get(lower(curriculum)) ?? curriculumPrefixes(curriculum))
    : index.all
  return stripCurriculumPrefix(name, prefixes)
}
